import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, StatsCards, ErrorState } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';
import { TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { useTransactions, useDeleteTransaction } from '../../hooks/useTransactions';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useProviders } from '../../hooks/useProviders';
import { useEmployees } from '../../hooks/useEmployees';
import { useCategories } from '../../hooks/useCategories';
import { useBranches } from '../../hooks/useBranches';
import InTransactionForm from '../../modules/transactions/InTransactionForm';
import OutTransactionForm from '../../modules/transactions/OutTransactionForm';
import { useTransactionsReport } from './useTransactionsReport';
import { useDebouncedValue } from '../Reports/useBanksReport';

interface TransactionsListProps {
  type: 'in' | 'out';
  title: string;
  description: string;
}

const fmt = (n: any) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const TransactionsList: React.FC<TransactionsListProps> = ({ type, title, description }) => {
  const [editingTransaction, setEditingTransaction] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');

  const queryOptions = useMemo(() => ({
    filters: { type: { $eq: type } },
    sort: [{ field: 'date', order: 'desc' as const }],
    pagination: { pageSize: 200 },
    // Relations (currency, bank/safe, invoice, bill, ...) must be populated so
    // the edit form can pre-fill every saved field
    populate: '*',
  }), [type]);

  const lookupOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);

  const { data: transactions, isLoading, error, refetch } = useTransactions(queryOptions);
  const { data: currencies = [] } = useCurrencies();
  const { data: providers = [] } = useProviders(lookupOptions);
  const { data: employees = [] } = useEmployees(lookupOptions);
  const { data: categories = [] } = useCategories(lookupOptions);
  const { data: branches = [] } = useBranches(lookupOptions);

  // Backend-based filtering + totals; falls back to client-side when unavailable
  const debouncedSearch = useDebouncedValue(searchTerm);
  const reportFilters = useMemo(() => ({
    currency: currencyFilter,
    dateFrom: dateFromFilter,
    dateTo: dateToFilter,
    minAmount: amountFromFilter,
    maxAmount: amountToFilter,
    category: categoryFilter,
    branch: branchFilter,
    search: debouncedSearch,
  }), [currencyFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, categoryFilter, branchFilter, debouncedSearch]);
  const report = useTransactionsReport(type, reportFilters);
  const { refetch: refetchReport } = report;

  const refetchAll = useCallback(() => { refetch(); refetchReport(); }, [refetch, refetchReport]);

  const deleteMutation = useDeleteTransaction(() => {
    setTimeout(() => refetchAll(), 100);
  });

  const getRecipientName = useCallback((t: any) => {
    if (!t.recipientId) return '-';
    const list: any[] = t.recipientType === 'employee' ? employees : providers;
    const match = list.find(
      (r: any) => r.documentId === t.recipientId || String(r.id) === String(t.recipientId)
    );
    return match ? match.name : '-';
  }, [employees, providers]);

  const getAccountName = useCallback((t: any) => {
    if (t.bank && typeof t.bank === 'object') return `${t.bank.name} (bank)`;
    if (t.safe && typeof t.safe === 'object') return `${t.safe.name} (safe)`;
    return '-';
  }, []);

  // In-transactions can be linked to an invoice-hire (or legacy invoice) record
  const getInvoiceLabel = useCallback((t: any) => {
    const inv = t.invoiceHire || t.invoice;
    if (!inv || typeof inv !== 'object') return '-';
    return inv.invoiceNumber || `#${inv.id}`;
  }, []);

  const applyLocalFilters = useCallback((list: any[]) => {
    return list.filter((t: any) => {
      const recipientName = getRecipientName(t);
      const matchesSearch = !searchTerm || [
        t.description,
        t.notes,
        recipientName,
        t.category?.name,
        t.branch?.name,
        t.bank?.name,
        t.safe?.name,
        t.chequeNumber,
        getInvoiceLabel(t),
      ].some((v: any) => v && v !== '-' && String(v).toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCurrency = currencyFilter === 'all' ||
        resolveCurrencyCode(t.currency, currencies) === currencyFilter;
      const txDate = new Date(t.date);
      const matchesDateFrom = !dateFromFilter || txDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || txDate <= new Date(dateToFilter);
      const matchesAmountFrom = !amountFromFilter || t.amount >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || t.amount <= parseFloat(amountToFilter);
      const matchesCategory = categoryFilter === 'all' ||
        t.category?.documentId === categoryFilter || String(t.category?.id) === categoryFilter;
      const matchesBranch = branchFilter === 'all' ||
        t.branch?.documentId === branchFilter || String(t.branch?.id) === branchFilter;
      return matchesSearch && matchesCurrency && matchesDateFrom && matchesDateTo &&
        matchesAmountFrom && matchesAmountTo && matchesCategory && matchesBranch;
    });
  }, [currencies, searchTerm, currencyFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, categoryFilter, branchFilter, getRecipientName, getInvoiceLabel]);

  // Rows shown in the table: server results when the report endpoint exists.
  // Local filters are re-applied as a safety net for params the backend
  // doesn't handle yet (e.g. category/branch).
  const rows = useMemo(
    () => applyLocalFilters(report.available ? report.results : (transactions || [])),
    [applyLocalFilters, report.available, report.results, transactions]
  );

  const statCards: StatCard[] = useMemo(() => {
    const s = report.summary;
    if (!report.available || !s) return [];
    return [
      { label: 'Transactions', value: s.totalTransactions ?? 0, color: 'blue' },
      { label: 'Total (EGP)', value: fmt(s.totalAmount), color: type === 'in' ? 'green' : 'red' },
      ...Object.entries(s.byCurrency || {}).map(([code, c]: [string, any]) => ({
        label: `Total ${code}`,
        value: fmt(c?.total),
        subtitle: `${c?.count ?? 0} transactions`,
        color: 'yellow' as const,
      })),
    ];
  }, [report.available, report.summary, type]);

  const buildExportRow = (t: any) => ({
    'Date': t.date ? new Date(t.date).toLocaleDateString() : '-',
    'Description': t.description || '-',
    'Amount': t.amount,
    'Currency': resolveCurrencyCode(t.currency, currencies),
    ...(type === 'out' ? {
      'Recipient': getRecipientName(t),
      'Category': t.category?.name || '-',
      'Vessel/Headquarters': t.branch?.name || '-',
    } : {
      'Invoice': getInvoiceLabel(t),
    }),
    'Account': getAccountName(t),
    'Cheque No': t.chequeNumber || '-',
    'Notes': t.notes || '-',
  });

  const handleExport = () => {
    exportToExcel(rows.map(buildExportRow), `${type}_transactions_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    printAsExcel(rows.map(buildExportRow), title);
  };

  const filters: FilterConfig[] = [
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [
      { value: 'all', label: 'All Currencies' },
      ...(currencies as any[])
        .filter((c: any) => c.isActive !== false && c.code)
        .map((c: any) => ({ value: c.code, label: c.code }))
    ] },
    ...(type === 'out' ? [
      { key: 'category', label: 'Category', type: 'select' as const, value: categoryFilter, onChange: setCategoryFilter, options: [
        { value: 'all', label: 'All Categories' },
        ...(categories as any[])
          .filter((c: any) => c.name && c.isActive !== false)
          .map((c: any) => ({ value: c.documentId || String(c.id), label: c.name }))
      ] },
      { key: 'branch', label: 'Vessel/HQ', type: 'select' as const, value: branchFilter, onChange: setBranchFilter, options: [
        { value: 'all', label: 'All Vessels/HQ' },
        ...(branches as any[])
          .filter((b: any) => b.name)
          .map((b: any) => ({ value: b.documentId || String(b.id), label: b.name }))
      ] },
    ] : []),
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const handleClearFilters = () => {
    setSearchTerm(''); setCurrencyFilter('all');
    setDateFromFilter(''); setDateToFilter('');
    setAmountFromFilter(''); setAmountToFilter('');
    setCategoryFilter('all'); setBranchFilter('all');
  };

  const columns: TableColumn[] = [
    { key: 'date', label: 'Date', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'description', label: 'Description', sortable: true },
    { key: 'amount', label: 'Amount', sortable: true, render: (value: number, row: any) => {
      const code = resolveCurrencyCode(row.currency, currencies);
      return `${code} ${Number(value || 0).toLocaleString()}`;
    } },
    ...(type === 'out' ? [
      { key: 'recipientId', label: 'Recipient', sortable: false, render: (_: any, row: any) => getRecipientName(row) },
      { key: 'category', label: 'Category', sortable: false, render: (value: any) => value?.name || '-' },
      { key: 'branch', label: 'Vessel/HQ', sortable: false, render: (value: any) => value?.name || '-' },
      { key: 'bill', label: 'Bill', sortable: false, render: (value: any) => value ? (value.description || `#${value.id}`) : '-' },
    ] as TableColumn[] : [
      { key: 'invoice', label: 'Invoice', sortable: false, render: (_: any, row: any) => getInvoiceLabel(row) },
    ] as TableColumn[]),
    { key: 'account', label: type === 'in' ? 'Deposited To' : 'Paid From', sortable: false, render: (_: any, row: any) => getAccountName(row) },
    { key: 'chequeNumber', label: 'Cheque No', sortable: false, render: (value: string) => value || '-' },
    { key: 'notes', label: 'Notes', sortable: false, render: (value: string) => value || '-' },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
  ];

  // Report-endpoint rows are leaner than Content Manager records; editing must
  // use the full record so every saved field pre-fills.
  const handleEdit = useCallback((row: any) => {
    const full = (transactions || []).find((t: any) =>
      (row.documentId && t.documentId === row.documentId) || String(t.id) === String(row.id));
    setEditingTransaction(full || row);
  }, [transactions]);

  const handleDelete = async (transaction: any) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try { await deleteMutation.execute(transaction); } catch (error) { console.error('Delete failed:', error); }
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} entityName="transactions" />;

  if (editingTransaction) {
    const handleSaved = () => {
      setEditingTransaction(null);
      refetchAll();
    };
    const handleCancel = () => setEditingTransaction(null);
    return (
      <div className="space-y-6">
        {type === 'in' ? (
          <InTransactionForm transaction={editingTransaction} onSaved={handleSaved} onCancel={handleCancel} />
        ) : (
          <OutTransactionForm transaction={editingTransaction} onSaved={handleSaved} onCancel={handleCancel} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />
      {statCards.length > 0 && (
        <StatsCards stats={statCards} columns={statCards.length >= 4 ? 4 : 3} />
      )}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search transactions..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={Math.max(transactions?.length ?? 0, rows.length)}
        filteredCount={rows.length}
        entityName="transactions"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={rows}
        onEdit={handleEdit}
        onDelete={handleDelete}
        title=""
        searchPlaceholder=""
        searchable={false}
      />
    </div>
  );
};

export default TransactionsList;
